/**
 * Bidirectional EN ↔ SL news slug registry.
 *
 * Populated as articles are loaded (KnowledgeHub list, NewsArticle detail) and
 * consulted synchronously by routing helpers (e.g. swapLang) so the language
 * toggle can swap `/sl/novice/<slug_sl>` ↔ `/en/news/<slug_en>` without a DB
 * round-trip.
 *
 * Persistence: sessionStorage so the map survives client-side navigations on
 * the current tab but never goes stale across deploys.
 */

const KEY = "kocevar.newsSlugMap.v1";

type Map = Record<string, string>;

function readStore(): { enToSl: Map; slToEn: Map } {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return { enToSl: {}, slToEn: {} };
}

function writeStore(s: { enToSl: Map; slToEn: Map }) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export function registerSlugPair(slugEn?: string | null, slugSl?: string | null) {
  if (!slugEn && !slugSl) return;
  const en = (slugEn || slugSl) as string;
  const sl = (slugSl || slugEn) as string;
  const store = readStore();
  if (store.enToSl[en] === sl && store.slToEn[sl] === en) return;
  store.enToSl[en] = sl;
  store.slToEn[sl] = en;
  writeStore(store);
}

export function translateSlug(slug: string, target: "en" | "sl"): string {
  const store = readStore();
  if (target === "sl") {
    // already SL? leave it. otherwise look up EN→SL.
    if (store.slToEn[slug]) return slug;
    return store.enToSl[slug] || slug;
  }
  // target === "en"
  if (store.enToSl[slug]) return slug;
  return store.slToEn[slug] || slug;
}
